import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ModeloDevsmartAI } from '../../modelos/DevsmartAI';
import { useAutenticacion } from './authContext';

export interface IMensajeChat {
    id: string;
    remitente: 'usuario' | 'agente';
    texto: string;
    fecha: Date;
    html?: string;
}

interface TipoContextoAsistenteIA {
    drawerAbierto: boolean;
    cargandoRespuesta: boolean;
    historialMensajes: IMensajeChat[];
    sesionCreada: boolean;
    abrirDrawer: () => void;
    cerrarDrawer: () => void;
    alternarDrawer: () => void;
    enviarMensaje: (texto: string) => Promise<void>;
    limpiarConversacion: () => void;
}

const ContextoAsistenteIA = createContext<TipoContextoAsistenteIA | undefined>(undefined);

interface PropsProveedorAsistenteIA {
    children: ReactNode;
}

const modeloDevsmartAI = new ModeloDevsmartAI();

export const ProveedorAsistenteIA: React.FC<PropsProveedorAsistenteIA> = ({ children }) => {
    const { usuario, estaAutenticado } = useAutenticacion();
    const [drawerAbierto, setDrawerAbierto] = useState<boolean>(false);
    const [cargandoRespuesta, setCargandoRespuesta] = useState<boolean>(false);
    const [historialMensajes, setHistorialMensajes] = useState<IMensajeChat[]>([]);
    const [dataSourceId, setDataSourceId] = useState<string | null>(null);
    const sincronizandoRef = useRef<boolean>(false);
    const intervaloRef = useRef<any>(null);
    const ultimoUsuarioRef = useRef<any>(null);

    // Cargar historial, estado inicial y dataSourceId desde localStorage al iniciar sesión o cambiar de usuario
    useEffect(() => {
        if (estaAutenticado && usuario) {
            ultimoUsuarioRef.current = usuario;
            const userSuffix = usuario.Email || String(usuario.Id) || 'default';
            const cachedHistory = localStorage.getItem(`conekt_chat_history_${userSuffix}`);
            const cachedOpen = localStorage.getItem(`conekt_chat_open_${userSuffix}`);
            const cachedDataSourceId = localStorage.getItem(`conekt_chat_datasource_id_${userSuffix}`);
            
            setDataSourceId(cachedDataSourceId);

            if (cachedHistory) {
                try {
                    const parsed = JSON.parse(cachedHistory);
                    const restored = parsed.map((m: any) => ({
                        ...m,
                        fecha: new Date(m.fecha)
                    }));
                    setHistorialMensajes(restored);
                } catch (e) {
                    // Sin logs por petición de limpieza
                }
            } else {
                setHistorialMensajes([]);
            }

            if (cachedOpen) {
                setDrawerAbierto(cachedOpen === 'true');
            } else {
                setDrawerAbierto(false);
            }
        } else {
            // Si cierra sesión o no está autenticado, borramos las claves del localStorage
            if (ultimoUsuarioRef.current) {
                const userSuffix = ultimoUsuarioRef.current.Email || String(ultimoUsuarioRef.current.Id) || 'default';
                
                localStorage.removeItem(`conekt_chat_history_${userSuffix}`);
                localStorage.removeItem(`conekt_chat_open_${userSuffix}`);
                localStorage.removeItem(`conekt_chat_width_${userSuffix}`);
                localStorage.removeItem(`conekt_chat_datasource_id_${userSuffix}`);
                ultimoUsuarioRef.current = null;
            }
            
            // Limpiar intervalos de la animación de escritura
            if (intervaloRef.current) {
                clearInterval(intervaloRef.current);
                intervaloRef.current = null;
            }
            
            // Resetear estados locales
            setHistorialMensajes([]);
            setDrawerAbierto(false);
            setDataSourceId(null);
        }
    }, [estaAutenticado, usuario]);

    // Guardar el historial en localStorage al actualizarse
    useEffect(() => {
        if (estaAutenticado && usuario) {
            const userSuffix = usuario.Email || String(usuario.Id) || 'default';
            localStorage.setItem(`conekt_chat_history_${userSuffix}`, JSON.stringify(historialMensajes));
        }
    }, [historialMensajes, estaAutenticado, usuario]);

    // Guardar el estado del cajón abierto/cerrado al actualizarse
    useEffect(() => {
        if (estaAutenticado && usuario) {
            const userSuffix = usuario.Email || String(usuario.Id) || 'default';
            localStorage.setItem(`conekt_chat_open_${userSuffix}`, drawerAbierto.toString());
        }
    }, [drawerAbierto, estaAutenticado, usuario]);

    // Sincronizar DataSource cuando el drawer se abre
    useEffect(() => {
        if (!estaAutenticado || !usuario || !drawerAbierto) return;

        const userSuffix = usuario.Email || String(usuario.Id) || 'default';

        const sincronizarDataSource = async () => {
            if (sincronizandoRef.current) return;
            sincronizandoRef.current = true;

            // Si ya hay un DataSource ID cargado en memoria, no hacemos nada
            if (dataSourceId) {
                sincronizandoRef.current = false;
                return;
            }

            const cachedDataSourceId = localStorage.getItem(`conekt_chat_datasource_id_${userSuffix}`);

            if (cachedDataSourceId) {
                setDataSourceId(cachedDataSourceId);
            } else {
                try {
                    const dataSource = await modeloDevsmartAI.obtenerDataSourcePorNombre('Conekt');
                    const dsId = dataSource?.id || dataSource?.Id || null;
                    setDataSourceId(dsId);
                    if (dsId) {
                        localStorage.setItem(`conekt_chat_datasource_id_${userSuffix}`, dsId);
                    }
                } catch (error) {
                    // Sin logs por petición de limpieza
                }
            }
            sincronizandoRef.current = false;
        };

        sincronizarDataSource();
    }, [drawerAbierto, estaAutenticado, usuario, dataSourceId]);

    // Limpiar intervalos de la animación si el componente se desmonta
    useEffect(() => {
        return () => {
            if (intervaloRef.current) {
                clearInterval(intervaloRef.current);
            }
        };
    }, []);

    const abrirDrawer = useCallback(() => {
        setDrawerAbierto(true);
    }, []);

    const cerrarDrawer = useCallback(() => {
        setDrawerAbierto(false);
    }, []);

    const alternarDrawer = useCallback(() => {
        setDrawerAbierto((prev) => !prev);
    }, []);

    const enviarMensaje = useCallback(async (texto: string) => {
        if (!texto.trim()) return;

        // Limpiar cualquier animación de escritura anterior si está activa
        if (intervaloRef.current) {
            clearInterval(intervaloRef.current);
            intervaloRef.current = null;
        }

        // 1. Agregar mensaje del usuario al historial
        const mensajeUsuario: IMensajeChat = {
            id: `usr-${Date.now()}`,
            remitente: 'usuario',
            texto: texto.trim(),
            fecha: new Date()
        };

        setHistorialMensajes((prev) => [...prev, mensajeUsuario]);
        setCargandoRespuesta(true);

        try {
            const userSuffix = usuario?.Email || (usuario?.Id ? String(usuario.Id) : '') || 'default';
            let respuestaTexto = "";
            let respuestaHtml = "";
            let currentDataSourceId = dataSourceId;

            // Si no tenemos el dataSourceId cargado en estado, intentamos recuperarlo por nombre
            if (!currentDataSourceId) {
                try {
                    const dataSource = await modeloDevsmartAI.obtenerDataSourcePorNombre('Conekt');
                    currentDataSourceId = dataSource?.id || dataSource?.Id || null;
                    setDataSourceId(currentDataSourceId);
                    if (currentDataSourceId) {
                        localStorage.setItem(`conekt_chat_datasource_id_${userSuffix}`, currentDataSourceId);
                    }
                } catch (dsError) {
                    // Sin logs por petición de limpieza
                }
            }

            // 3. Intentar consulta directa de base de datos (NL2SQL) mediante datasource/query
            if (currentDataSourceId) {
                try {
                    const resultadoQuery = await modeloDevsmartAI.consultarDatos(currentDataSourceId, texto);
                    respuestaTexto = resultadoQuery.narrativeResult || resultadoQuery.NarrativeResult || "";
                    respuestaHtml = resultadoQuery.html || resultadoQuery.Html || "";
                } catch (queryError) {
                    // Fallback de cortesía local en español para preguntas no traducibles a SQL
                    const preguntaMin = texto.toLowerCase().trim();
                    if (preguntaMin.includes("hola") || preguntaMin.includes("buenos") || preguntaMin.includes("tardes") || preguntaMin.includes("noches")) {
                        respuestaTexto = "¡Hola! Soy tu asistente de IA. Estoy aquí para ayudarte a analizar y consultar la operación de Conekt en tiempo real. Puedes preguntarme por tareas en ejecución, emergencias o mantenimientos pendientes.";
                    } else if (preguntaMin.includes("gracias") || preguntaMin.includes("ok") || preguntaMin.includes("entendido")) {
                        respuestaTexto = "¡De nada! Si tienes más preguntas sobre las tareas, emergencias o técnicos, solo dímelo.";
                    } else {
                        respuestaTexto = "No logré encontrar registros o estructurar una consulta para tu pregunta. Por favor, intenta reformularla enfocándote en aspectos de la operación como servicios, técnicos o emergencias.";
                    }
                }
            } else {
                respuestaTexto = "No se ha configurado un origen de datos activo para realizar consultas en este momento.";
            }

            const idMensajeAgente = `ai-${Date.now()}`;
            const mensajeAgente: IMensajeChat = {
                id: idMensajeAgente,
                remitente: 'agente',
                texto: '', // Empieza vacío para la animación de escritura
                fecha: new Date()
            };

            setHistorialMensajes((prev) => [...prev, mensajeAgente]);
            setCargandoRespuesta(false); // Apagar el indicador de carga para mostrar el globo de texto

            let index = 0;
            // El paso determina cuántos caracteres se agregan por paso para mantener el tiempo de escritura.
            const paso = Math.max(1, Math.ceil(respuestaTexto.length / 120));

            const intervalo = setInterval(() => {
                index += paso;
                const finalIndex = Math.min(index, respuestaTexto.length);
                const nuevoTexto = respuestaTexto.substring(0, finalIndex);

                setHistorialMensajes((prev) => 
                    prev.map((msg) => 
                        msg.id === idMensajeAgente ? { ...msg, texto: nuevoTexto } : msg
                    )
                );

                if (finalIndex >= respuestaTexto.length) {
                    clearInterval(intervalo);
                    intervaloRef.current = null;

                    if (respuestaHtml) {
                        setHistorialMensajes((prev) => 
                            prev.map((msg) => 
                                msg.id === idMensajeAgente ? { ...msg, html: respuestaHtml } : msg
                            )
                        );
                    }
                }
            }, 25);

            intervaloRef.current = intervalo;
        } catch (error) {
            const mensajeError: IMensajeChat = {
                id: `err-${Date.now()}`,
                remitente: 'agente',
                texto: "Lo siento, ha ocurrido un error al conectar con el servidor del asistente. Por favor, intenta de nuevo más tarde.",
                fecha: new Date()
            };
            setHistorialMensajes((prev) => [...prev, mensajeError]);
        } finally {
            setCargandoRespuesta(false);
        }
    }, [dataSourceId, usuario]);

    const limpiarConversacion = useCallback(() => {
        if (intervaloRef.current) {
            clearInterval(intervaloRef.current);
            intervaloRef.current = null;
        }
        setHistorialMensajes([]);

        if (usuario) {
            const userSuffix = usuario.Email || String(usuario.Id) || 'default';
            localStorage.removeItem(`conekt_chat_history_${userSuffix}`);
        }
    }, [usuario]);

    const valor: TipoContextoAsistenteIA = {
        drawerAbierto,
        cargandoRespuesta,
        historialMensajes,
        sesionCreada: !!dataSourceId,
        abrirDrawer,
        cerrarDrawer,
        alternarDrawer,
        enviarMensaje,
        limpiarConversacion: limpiarConversacion as any // Typecast for backward compatibility with consumer interfaces
    };

    return (
        <ContextoAsistenteIA.Provider value={valor}>
            {children}
        </ContextoAsistenteIA.Provider>
    );
};

export const useAsistenteIA = (): TipoContextoAsistenteIA => {
    const contexto = useContext(ContextoAsistenteIA);
    if (contexto === undefined) {
        throw new Error('useAsistenteIA debe ser usado dentro de un ProveedorAsistenteIA');
    }
    return contexto;
};
